import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark, BookmarkCheck, Folder } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { collectionsAPI } from '../services/api';

const CACHE_TTL_MS = 1000 * 60 * 5; // 5 minutes
const promptCollectionsCache = new Map();

/**
 * BookmarkButton - Add/remove prompts from collections
 * Shows bookmark icon, opens modal to select collection(s)
 */
const BookmarkButton = ({ promptId, className = '' }) => {
  const { currentUser } = useAuth();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collections, setCollections] = useState([]);
  const [promptCollections, setPromptCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if prompt is bookmarked (in any collection)
  const loadPromptCollections = useCallback(async () => {
    if (!promptId) return;

    const now = Date.now();
    const cached = promptCollectionsCache.get(promptId);

    if (cached?.data) {
      setPromptCollections(cached.data);
      setIsBookmarked(cached.data.length > 0);

      if (cached.expiry && cached.expiry > now) {
        return; // Fresh cache hit
      }
    }

    let fetchPromise = cached?.promise;

    if (!fetchPromise) {
      fetchPromise = collectionsAPI
        .getCollectionsForPrompt(promptId)
        .then((response) => {
          const data = response.data.collections || [];
          promptCollectionsCache.set(promptId, {
            data,
            expiry: Date.now() + CACHE_TTL_MS,
          });
          return data;
        })
        .catch((err) => {
          promptCollectionsCache.delete(promptId);
          throw err;
        });

      promptCollectionsCache.set(promptId, {
        ...(cached?.data ? { data: cached.data, expiry: cached.expiry } : {}),
        promise: fetchPromise,
      });
    }

    try {
      const data = await fetchPromise;
      setPromptCollections(data);
      setIsBookmarked(data.length > 0);
    } catch (err) {
      console.error('Error loading prompt collections:', err);
    }
  }, [promptId]);

  useEffect(() => {
    if (currentUser && promptId) {
      loadPromptCollections();
    } else {
      setPromptCollections([]);
      setIsBookmarked(false);
    }
  }, [currentUser, promptId, loadPromptCollections]);

  const loadUserCollections = async () => {
    try {
      setLoading(true);
      const response = await collectionsAPI.getAll();
      setCollections(response.data.collections || []);
      setError(null);
    } catch (err) {
      console.error('Error loading collections:', err);
      setError('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const handleBookmarkClick = async () => {
    if (!currentUser) {
      alert('Please sign in to bookmark prompts');
      return;
    }

    await loadUserCollections();
    setShowCollectionModal(true);
  };

  const handleToggleCollection = async (collectionId) => {
    const isInCollection = promptCollections.some((c) => c.id === collectionId);

    try {
      if (isInCollection) {
        await collectionsAPI.removePrompt(collectionId, promptId);
      } else {
        await collectionsAPI.addPrompt(collectionId, promptId);
      }

      promptCollectionsCache.delete(promptId);
      await loadPromptCollections();
    } catch (err) {
      console.error('Error toggling collection:', err);
      alert(err.response?.data?.error || 'Failed to update collection');
    }
  };

  const handleCreateCollection = async () => {
    const name = prompt('Enter collection name:');
    if (!name || name.trim().length === 0) return;

    try {
      const response = await collectionsAPI.create({
        name: name.trim(),
        description: '',
        isPublic: false,
      });

      // Add to collections list
      setCollections((prev) => [response.data.collection, ...prev]);

      // Automatically add prompt to new collection
      await collectionsAPI.addPrompt(response.data.collection.id, promptId);
      promptCollectionsCache.delete(promptId);
      await loadPromptCollections();
    } catch (err) {
      console.error('Error creating collection:', err);
      alert(err.response?.data?.error || 'Failed to create collection');
    }
  };

  if (!currentUser) {
    return null; // Don't show bookmark button to non-authenticated users
  }

  return (
    <>
      <button
        onClick={handleBookmarkClick}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
          isBookmarked
            ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
        } ${className}`}
        title={isBookmarked ? `In ${promptCollections.length} collection(s)` : 'Add to collection'}
      >
        {isBookmarked ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
        <span className="text-sm font-medium">
          {isBookmarked ? promptCollections.length : 'Save'}
        </span>
      </button>

      {/* Collection Selection Modal */}
      {showCollectionModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowCollectionModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Save to Collection</h3>
              <button
                onClick={() => setShowCollectionModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Close"
              >
                X
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading collections...</div>
            ) : (
              <>
                {/* Create New Collection */}
                <button
                  onClick={handleCreateCollection}
                  className="w-full mb-4 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
                >
                  <Folder size={18} />
                  Create New Collection
                </button>

                {/* Collection List */}
                {collections.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No collections yet. Create one to get started!
                  </div>
                ) : (
                  <div className="space-y-2">
                    {collections.map((collection) => {
                      const isInCollection = promptCollections.some((c) => c.id === collection.id);

                      return (
                        <button
                          key={collection.id}
                          onClick={() => handleToggleCollection(collection.id)}
                          className={`w-full px-4 py-3 rounded-lg border transition-colors text-left ${
                            isInCollection
                              ? 'border-blue-600 bg-blue-50 dark:bg-blue-900 dark:border-blue-500'
                              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Bookmark
                                size={18}
                                className={
                                  isInCollection ? 'fill-current text-blue-600 dark:text-blue-300' : ''
                                }
                              />
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {collection.name}
                                </div>
                                {collection.description && (
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {collection.description}
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {collection.prompt_count || 0} prompts
                                </div>
                              </div>
                            </div>
                            {isInCollection && (
                              <BookmarkCheck size={20} className="text-blue-600 dark:text-blue-300" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BookmarkButton;
